/** The counts on the rail: what is outstanding rather than where to go. (The bell has its own
 * count, from the notification inbox, in `AppShell`.)
 *
 * `docs/design/35-page1-target.md` item 4. The rail stops being a list of destinations and becomes a
 * view of what is waiting. Three rules decide what may be drawn here, and each is a defect this
 * product has had in another place:
 *
 * - **Only a count that is real.** Each badge reads a list the server already answers, from the
 *   query cache, and states what it counts in words for a screen reader (*"open"*, *"waiting on
 *   you"*), because a bare "7" names nothing.
 * - **No new API call to populate decoration.** Tasks makes the Tasks screen's own request (same
 *   cache entry, so one poll), and Action items reads **the bell's query** (`useWaitingOnMe`: one
 *   number for what is waiting on a person, G-Wapprover-01). Source coverage is **passive**:
 *   `queryFn: skipToken` means the observer *reads whatever a screen has already fetched* and never
 *   fetches, here the census Readiness holds for the window in force. Before that screen has been
 *   opened there is nothing to show, and the badge draws **nothing at all**: not a `0`, which would
 *   say "nothing is missing", and not a `?`, which would say a read failed that was never made. The
 *   honest cost is that it appears once its screen has been visited; the fix that removes the cost
 *   is one cheap server endpoint for the rail's counts, and that is a decision, not a thing to do in
 *   passing in the shell.
 * - **Every count says what it counts** (G-Wadmin-18). Three badges sit on the rail beside a bell
 *   and a Home tile, and a bare 64 beside a "Needs you 0" reads as two answers to one question. Each
 *   badge carries a hover and a screen-reader sentence naming its noun: *open tasks*, *waiting on
 *   you*, *closed periods with no reading*. Only the second is the bell's number.
 * - **A failed count is never a zero.** A query that failed is drawn `?`, in the warning colour,
 *   with a title that says what could not be read (EXP-29). Success with nothing to count is drawn
 *   as nothing and said as *"none"* to a screen reader.
 *
 * Every badge carries `data-state` (`unread` or `counted`) and, when counted, `data-count`; Tasks
 * also carries `data-capped`. Those are the attributes the tests and the browser gate read.
 */

import { skipToken, useQuery } from "@tanstack/react-query";
import type { ComponentType } from "react";

import type { Coverage } from "../api/sources";
import { sourceKeys } from "../api/sources";
import { useWaitingOnMe } from "../api/waiting";
import { useTasks } from "../api/work";
import type { Window } from "../screens/useScreen";
import styles from "./AppShell.module.css";

/** How many tasks the server returns when it is not asked for a limit — `limit` on `GET /tasks`.
 *
 * **A mirror, and the reason it is here is the badge's honesty.** The count is the length of a
 * list, and a list cut at a page boundary is not a count: 50 returned may mean 50 or 500. At the
 * boundary the badge says "50+" rather than a number it cannot stand behind. If the server's default
 * moves, the worst outcome is a badge that says "50+" at 100, which understates and says so.
 */
const TASK_PAGE = 50;

/** What a badge knows. `none` is *nothing to say yet*, and it is a different state from a count of
 * zero: the first draws nothing because nothing was read, the second draws nothing because there is
 * nothing to do. Only the second is a statement. */
export type CountRead =
  | { kind: "none" }
  | { kind: "unread" }
  | { kind: "counted"; count: number; capped: boolean };

/** The signed-in person's open tasks — the same query the Tasks screen opens on (`mine`, `open`),
 * so one cache entry, no second poll, and closing a task (which invalidates `["work"]`) moves the
 * rail with no code of their own. */
export function useOpenTasks(): CountRead {
  const { data, error, isPending } = useTasks(true, "open");
  if (isPending && !error) return { kind: "none" };
  if (error || !data) return { kind: "unread" };
  return { kind: "counted", count: data.length, capped: data.length >= TASK_PAGE };
}

/** Everything waiting on this person: **the bell's number and the Waiting on you tab's** - one read
 * (`useWaitingOnMe`), one query key, so it costs the server nothing the bell does not.
 *
 * This badge was a *passive* read of the approvals queue alone, so it said 3 while the bell and
 * the tab, which also count tasks and bills to review, said 4 (G-Wapprover-01). Two counts of "what
 * waits on me" that differ are worse than one that is late. Still reading draws nothing, a failed
 * read is `?` and never zero. */
function useWaiting(): CountRead {
  const { data, error } = useWaitingOnMe();
  if (error) return { kind: "unread" };
  if (data === undefined) return { kind: "none" };
  return { kind: "counted", count: data.total, capped: false };
}

/** Closed windows with no bill or reading and no declared nil, for the window in force. Passive,
 * and keyed on the same dates the census is read with, so it can only ever show the number for the
 * window the figures on screen are for. */
export function useMissingSources(period: Window | undefined): CountRead {
  const { data, error } = useQuery<Coverage>({
    queryKey: sourceKeys.coverage(period?.period_start ?? "none", period?.period_end ?? "none"),
    queryFn: skipToken,
  });
  // **A shape this cannot read is never a crash.** `data.completeness.missing` without the
  // optional chain threw on any `/sources` body lacking `completeness`, and because the rail is
  // mounted on *every* screen that took the whole application down - the worst possible blast
  // radius for one absent field. Found by the Readiness agent, whose stubs omitted it: 19 tests
  // died at once, which is what a crash in shared chrome looks like.
  //
  // `unread` rather than `none`, and never `0`: we asked, something came back, and we could not
  // count it. A missing count that renders as zero is the `data ?? []` defect that once made a
  // failed read print "nothing owed" on Readiness.
  const missing = data?.completeness?.missing;
  if (typeof missing === "number") return { kind: "counted", count: missing, capped: false };
  if (error ?? data) return { kind: "unread" };
  return { kind: "none" };
}

/** The tab a rail item opens on **when it carries a count that another tab holds** (U2). The Processing
 * badge counts closed windows with no bill, which are the *Missing* tab's rows; the item used to open
 * on *To review*, which said "Everything that arrived is decided" beside a badge reading 28. So the
 * item opens on the tab the number is about while the number is above nothing, and on its first tab
 * otherwise (ADR-0212: Processing opens on To review). `undefined` leaves the item where it was. */
export function railTabFor(item: string, missing: CountRead): string | undefined {
  if (item === "processing" && missing.kind === "counted" && missing.count > 0) return "missing";
  return undefined;
}

/** What a count is a count *of*, said to a screen reader after the number. */
interface Noun {
  /** After a number: "3 open". */
  some: string;
  /** For zero, said and not drawn. */
  none: string;
  /** When the read failed. */
  unread: string;
  /** What a count of this is, for a hover: "28 closed periods with no reading". A bare number on the
   * rail was read as a second count of "what needs me" (G-Wadmin-18). */
  title: (count: number, capped: boolean) => string;
}

function RailBadge({ testid, read, noun }: { testid: string; read: CountRead; noun: Noun }) {
  if (read.kind === "none") return null;
  if (read.kind === "unread") {
    return (
      <span
        className={styles.navBadge}
        data-testid={testid}
        data-state="unread"
        title={noun.unread}
      >
        ?<span className="visuallyHidden"> {noun.unread}</span>
      </span>
    );
  }
  const { count, capped } = read;
  return (
    <span
      className={count === 0 ? undefined : styles.navBadge}
      data-testid={testid}
      data-state="counted"
      data-count={String(count)}
      data-capped={capped ? "yes" : "no"}
      title={count === 0 ? undefined : noun.title(count, capped)}
    >
      {count === 0 ? null : capped ? `${TASK_PAGE}+` : count}
      <span className="visuallyHidden">{count === 0 ? ` ${noun.none}` : ` ${noun.some}`}</span>
    </span>
  );
}

/** The count on "Tasks & requests" — **S-10**. The testid and the visually-hidden words are the
 * ones the first version had; `nav-badge-tasks` is read by the shell tests and by the overflow
 * gate, which waits for it to be *attached* (a count of nothing draws no pill, only its words). */
function TasksBadge() {
  return (
    <RailBadge
      testid="nav-badge-tasks"
      read={useOpenTasks()}
      noun={{
        some: "open",
        // "Home none open" was the first thing a screen reader said on every page; "no open tasks" is a
        // sentence (review item 18).
        none: "no open tasks",
        unread: "your open tasks could not be read",
        title: (count, capped) =>
          `${capped ? `${String(TASK_PAGE)}+` : String(count)} open ${count === 1 && !capped ? "task" : "tasks"}`,
      }}
    />
  );
}

function ApprovalsBadge() {
  return (
    <RailBadge
      testid="nav-badge-approvals"
      read={useWaiting()}
      noun={{
        some: "waiting on you",
        none: "nothing waiting on you",
        unread: "what is waiting on you could not be read",
        title: (count) => `${String(count)} waiting on you`,
      }}
    />
  );
}

function SourcesBadge({ period }: { period: Window | undefined }) {
  return (
    <RailBadge
      testid="nav-badge-sources"
      read={useMissingSources(period)}
      noun={{
        some: "missing bills",
        none: "no missing bills",
        unread: "the expected bills could not be read",
        title: (count) =>
          `${String(count)} missing ${count === 1 ? "bill" : "bills"}`,
      }}
    />
  );
}

/** Which rail entry carries which badge, by place (`routeTable.ts`'s `NavId`). Kept here rather than as a field on the nav items
 * so adding a count is one line in one file and the list of destinations (`navItems.ts`) stays a
 * list of destinations. */
export const BADGES: Readonly<Record<string, ComponentType<{ period: Window | undefined }>>> = {
  // The open-task count rides Home: "Needs you" is Home's queue, and Tasks & requests is its tab.
  home: TasksBadge,
  actions: ApprovalsBadge,
  processing: SourcesBadge,
};
